import React, { ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import SubTaskView from "./SubTaskView";
import { Button, CircularProgress, IconButton, Tooltip, Typography } from "@mui/material";
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import BorderLinearProgress from "../../../BorderLinearProgress";
import { AcousticTask, Session, SubTask } from "./models";
import { useApiService } from "../../../../services/ApiServiceProvider";
import { ApiAcousticTask, ApiFile, ApiRecording } from "../../../../services/ApiService";
import { useStorage } from "../../../../services/StorageProvider";
import { useLocalStorage } from "../../../../services/LocalStorageProvider";
import { useDeviceCollector } from "../../../../hooks/useDeviceCollector";
import { Status } from "../../models";
import { Alert } from '@mui/material';
import { i18nLanguageToLabel } from "../../../utils";
import TutorialDialog from "./TutorialDialog";

const TaskView = ({task, content, showAudioBar = false, onCompletion, onChange, onIsRecordingChange, onStreamChange, buttonLabel = "Next", createQuestionnaire = undefined} : {
  task: AcousticTask
  content?: ReactNode,
  showAudioBar?: boolean
  onCompletion: () => void
  onChange?: (updatedTask: AcousticTask, currentTaskIndex: number) => void
  onIsRecordingChange?: (isRecording: boolean) => void;
  onStreamChange?: (stream: MediaStream) => void;
  buttonLabel?: string;
  createQuestionnaire?: () => Promise<void>;
}) => {
  const [subTasks, setSubTasks] = useState<SubTask[]>(() => [...task.subTasks]);
  const [currentTaskIndex, setCurrentTaskIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const apiService = useApiService();
  const { storage, updateStorage } = useStorage();
  const session: Session = storage.session;
  const participant: any = storage.participant;
  const [showAlert, setShowAlert] = useState(false);
  const [alertSeverity, setAlertSeverity] = useState<'error' | 'warning' | 'info' | 'success'>('info');
  const [alertMessage, setAlertMessage] = useState<string>('');
  const [showTutorial, setShowTutorial] = useState(false);
  const { t, i18n } = useTranslation(task.cohort);
  const { updateStorage: updateLocalStorage } = useLocalStorage();
  const { collect: collectDeviceInfo } = useDeviceCollector();

  const handleOpenTutorial = () => {
    updateLocalStorage({ disableRecording: true });
    setShowTutorial(true);
  };

  const handleCloseTutorial = () => {
    setShowTutorial(false);
    updateLocalStorage({ disableRecording: false });
  };

  useEffect(() => {
    setSubTasks([...task.subTasks]);
  }, [task.subTasks]);

  useEffect(() => {
    const targetProgress = (currentTaskIndex + 1) / (subTasks.length + 1) * 100;
    setProgress(targetProgress);
  }, [subTasks]);

  const handleAudioRecorded = (audioSource: string, index: number) => {
    subTasks[index].audioSource = audioSource;
    const updatedSubTasks = [...subTasks];
    task.subTasks = updatedSubTasks;
    setSubTasks(updatedSubTasks);
    setCurrentTaskIndex(currentTaskIndex + 1);
    onChange?.(task, currentTaskIndex);
  };

  const uploadRecordingFile = async (subTask: SubTask, site: string) : Promise<string> => {
    if (!subTask.audioSource) { throw new Error('Audio source is null'); }
    if (!subTask.name) { throw new Error('Sub task name is null'); }

    const file = {
      uri: subTask.audioSource,
      name: `${subTask.id}.wav`,
      type: 'audio/wav'
    } as ApiFile;
    return await apiService.uploadFile(file, site.toUpperCase());
  };

  const updateTaskInSession = () => {
    const taskFoundIndex = session.acousticTasks.findIndex(t => t.name === task.name);
    if (taskFoundIndex > -1) {
      session.acousticTasks[taskFoundIndex] = task;
    } else {
      session.acousticTasks.push(task);
    }
    console.log('Session updated after acoustic task completed');
    updateStorage({ session });
  }

  const createAcousticTask = async (): Promise<ApiAcousticTask> => {
    try {
      return await apiService.createAcousticTask({
        id: task.id,
        subjectId: session.subjectId,
        sessionId: session.id,
        name: task.name,
        cohort: task.cohort as string,
        startedAt: task.startedAt,
        status: 'completed',
        completedAt: new Date().toISOString(),
        language: i18nLanguageToLabel(i18n.language)
      });
    } catch (error) {
      console.log(error);
      throw error
    }
  }

  const createRecordings = async (): Promise<void> => {
    if (!participant.site) {
      throw new Error('Participant site is null');
    }

    try {
      const deviceInfo = await collectDeviceInfo();
      const apiRecordings: ApiRecording[] = [];
      for (const subTask of subTasks) {
        if (!subTask.audioSource) {
          throw new Error('Audio source is null');
        }

        const blob = await (await fetch(subTask.audioSource)).blob();
        const audio = new Audio(subTask.audioSource);
        const duration = await new Promise<number>((resolve) => { // Duration in seconds
          audio.onloadedmetadata = () => resolve(audio.duration);
        });

        const recordingFilePath = await uploadRecordingFile(subTask, participant.site);
        const recording: ApiRecording = {
          id: subTask.id,
          acousticTaskId: task.id,
          subjectId: participant.id,
          sessionId: session.id,
          name: subTask.name,
          filePath: recordingFilePath,
          createdAt: subTask.createdAt!,
          duration: duration,
          size: blob.size,
          profileName: subTask.profileName ?? null,
          profile: subTask.profile ?? 'v1.0.0',
          inputGain: `${subTask.inputGain}`,
          microphone: subTask.microphone || 'Web Audio',
          platform: deviceInfo.platform,
          platformVersion: deviceInfo.platformVersion,
          platformArchitecture: deviceInfo.platformArchitecture,
          browser: deviceInfo.browser,
          browserVersion: deviceInfo.browserVersion,
          screenResolution: deviceInfo.screenResolution,
          windowResolution: deviceInfo.windowResolution,
          deviceInfo: deviceInfo.deviceInfo,
          deviceType: deviceInfo.deviceType,
          touchScreen: deviceInfo.touchScreen,
        };
        apiRecordings.push(recording);
      }
      console.log('Creating recordings:', apiRecordings);
      if (apiRecordings.length) {
        await apiService.createRecordings(apiRecordings);
      } else {
        throw new Error('No recordings found');
      }

    } catch (error) {
      console.log(error);
      throw error;
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    if (showAlert) { setShowAlert(false); }

    try {
      // 1. Create recordings
      const _recordingUrls = await createRecordings();

      // 2.a Create acoustic task
      const newTask = await createAcousticTask();
      task.completedAt = newTask.completedAt;
      task.status = newTask.status as Status;

      // 2.b Submit any questionnaire associated with this task
      if (createQuestionnaire) {
        await createQuestionnaire();
      }

      // 3. Update the session object in storage
      updateTaskInSession();

      // 4. Remove the files from the device
      deleteAudioFiles(subTasks);

      onCompletion();
    } catch (error: Error | any) {
      console.log('message:', error.message);
      if (error.message === 'Internal Server Error, please try again later') {
        setAlertSeverity('error');
        setAlertMessage(t('There was an error with the data submission. Please try again. If the problem persists, give us a few minutes and try again.'));
        setShowAlert(true);
      } else if (error?.message === 'Forbidden, you do not have permissions to access this resource') {
        setAlertSeverity('error');
        setAlertMessage(t("You don't have permissions to submit this task."));
        setShowAlert(true);
      } else if (error?.message === 'Unauthorized, please try logging out and back in again') {
        setAlertSeverity('error');
        setAlertMessage(t("You are not authorized to submit this task. Please try logging out and back in again."));
        setShowAlert(true);
      } else if (error?.message.startsWith('HTTP error!')) {
        setAlertSeverity('error');
        setAlertMessage(t('There was an error with the data submission. Please try again. If the problem persists, give us a few minutes and try again.'));
        setShowAlert(true);
      } else {
        setAlertSeverity('error');
        setAlertMessage(t('An unexpected error occurred.') + ` ${error.message}`);
        setShowAlert(true);}
    }

    setSubmitting(false);
  };

  const shouldRenderTaskContent = currentTaskIndex < subTasks.length;

  return (
    <div className="task">
      {subTasks.length > 1 && <BorderLinearProgress
        variant="determinate"
        value={progress}
      />}
      <div style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant='h3' component='h1' id='page-heading' tabIndex={-1} sx={{ mb: 2 }}>{t(task.title)}</Typography>
          {task.tutorialUrl && (
            <Tooltip title={t("Tutorial")}>
              <IconButton onClick={handleOpenTutorial} color="primary" aria-label={t("Tutorial")} size="large">
                <OndemandVideoIcon fontSize="large" />
              </IconButton>
            </Tooltip>
          )}
        </div>
        {shouldRenderTaskContent && task.description && <Typography variant='body1'>{t(task.description)}</Typography>}
      </div>
      {task.tutorialUrl && (
        <TutorialDialog open={showTutorial} tutorialUrl={task.tutorialUrl} onClose={handleCloseTutorial} />
      )}
      {showAlert && <Alert severity={alertSeverity}>{alertMessage}</Alert>}
      {shouldRenderTaskContent && content}
      {
        subTasks.map((subTask, index) => (
        <React.Fragment key={index}>
          {index === currentTaskIndex && (
            <>
            <SubTaskView
              onAudioRecorded={(audioBlob) => handleAudioRecorded(audioBlob, index)}
              onIsRecordingChange={onIsRecordingChange}
              onStreamChange={onStreamChange}
              showAudioBar={showAudioBar}
              subTask={subTask}
              buttonLabel={buttonLabel}
              tutorialOpen={showTutorial}>
                <>
                </>
            </SubTaskView>
            </>
          )}
        </React.Fragment>
        ))
      }
      {!shouldRenderTaskContent && (
        <div style={{ textAlign: 'center' }}>
          <img src="assets/images/b2ai-thank-you.jpg" alt="Thank you" style={{ maxHeight: '250px' }} />
          <div style={{ minHeight: '4rem' }}>
            {submitting && <CircularProgress />}
          </div>

          <Button variant="contained"
            disabled={submitting}
            onClick={handleSubmit}
          >
            {t("Done")}
          </Button>
        </div>
      )}
    </div>
  );
}

export default TaskView;

function deleteAudioFiles(_subTasks: SubTask[]) {
  console.log('Deleteing files has not been implemented yet');
}
